import { Component, inject, Input, numberAttribute, OnInit, Signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { PatientProvider } from '../../../../care-team/models/patient-provider';
import { getPatientCareTeam } from '../../../../care-team/store/actions/care-team.actions';
import { selectCareTeamMemberById } from '../../../../care-team/store/selectors/care-team.selector';
import { confirmCancel, loadPageWithPatientIdParameter } from '../../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../../core/store/selectors/app.selector';
import { MessageRequest } from '../../../../messages/models/message-request';
import { sendMessage } from '../../../../messages/store/actions/messages.actions';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../../shared/components/progress-spinner/progress-spinner.component';
import { selectUser } from '../../../../user/store/selectors/user.selectors';
import { visitTypes } from '../../../data/visit-type';
import {
  closeScheduleAppointmentDialog,
  scheduleAppointmentDialogNextStep,
} from '../../../store/actions/appointments.actions';

@Component({
  selector: 'app-appointment-selection',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    PageHeaderComponent,
    ProgressSpinnerComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './appointment-selection.component.html',
  styleUrl: './appointment-selection.component.scss',
})
export class AppointmentSelectionComponent implements OnInit {
  private store = inject(Store);
  patientCareTeamMember!: Signal<PatientProvider | null>;
  providerId!: string;
  user = this.store.selectSignal(selectUser);
  @Input() set providerUserId(value: string) {
    this.providerId = value;
    this.patientCareTeamMember = this.store.selectSignal(selectCareTeamMemberById(this.providerId));
  }
  @Input({ transform: numberAttribute }) patientId!: number;
  @Input({ transform: numberAttribute }) teamId: number | null = null;
  @Input({ transform: numberAttribute }) visitTypeId!: number;
  @Input() isModal: boolean = false;

  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);

  requestForm!: FormGroup;
  private initialFormValue: unknown;

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());

    this.requestForm = new FormGroup(
      {
        firstAvailable: new FormControl<boolean>(false),
        dayTime: new FormGroup({
          Monday: new FormControl<boolean>(false),
          Tuesday: new FormControl<boolean>(false),
          Wednesday: new FormControl<boolean>(false),
          Thursday: new FormControl<boolean>(false),
          Friday: new FormControl<boolean>(false),
          timeOfDay: new FormControl<string>('', Validators.required),
        }),
      },
      { validators: this.atLeastOneSelectionRequired }
    );

    this.initialFormValue = this.requestForm.getRawValue();
  }

  atLeastOneSelectionRequired(control: AbstractControl): ValidationErrors | null {
    const firstAvailable = control.get('firstAvailable')?.value;
    const dayTimeGroup = control.get('dayTime') as FormGroup;

    const anyDaySelected = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].some(
      day => dayTimeGroup.get(day)?.value
    );

    if (!firstAvailable && !anyDaySelected) {
      return { requiredSelection: true };
    }

    return null;
  }

  onFirstAvailableChange(checked: boolean) {
    if (checked) {
      this.requestForm.controls['dayTime'].disable();
    } else {
      this.requestForm.controls['dayTime'].enable();
    }
  }

  requestAppointment() {
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      return;
    }

    let appointmentDayTime = '';
    if (this.requestForm.get('firstAvailable')?.value) {
      appointmentDayTime = 'first available appointment';
    } else {
      const dayTimeGroup = this.requestForm.get('dayTime') as FormGroup;
      const selectedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].filter(
        day => dayTimeGroup.get(day)?.value
      );
      const timeOfDay = dayTimeGroup.get('timeOfDay')?.value;

      appointmentDayTime = `an appointment on ${
        selectedDays.length === 0
          ? ''
          : selectedDays.length === 1
            ? selectedDays[0]
            : selectedDays.slice(0, -1).join(', ') + ' or ' + selectedDays.slice(-1)
      } in the ${timeOfDay}`;
    }

    const careTeamMember = this.patientCareTeamMember();
    const user = this.user();
    if (careTeamMember === null || user === null) return;

    const team = careTeamMember.teams.find(team => team.teamId === this.teamId);
    let locationName = null;
    if (team !== undefined) {
      locationName = team.facilityName === '' ? null : team.facilityName;
    }

    const message = `Appointment requested with ${careTeamMember.displayName} for ${visitTypes[this.visitTypeId - 1].description}${locationName === null ? '' : ' at ' + locationName}. Patient requests ${appointmentDayTime}. Please contact patient via preferred contact method to confirm appointment.`;

    const messageRequest: MessageRequest = {
      senderDisplayName: user.firstName + ' ' + user.lastName,
      messageTypeId: 6,
      teamworkWorkItemTypeId: 11, // TeamworkWorkItemType.AppointmentRequest
      providerUserId: careTeamMember.providerUserId,
      providerTeamId: careTeamMember.teams.find(team => team.isPrimary)?.teamId ?? 0,
      subject: `Portal Request - ${visitTypes[this.visitTypeId - 1].description}`,
      body: message,
    };

    const continueAction = this.isMobileBrowser()
      ? loadPageWithPatientIdParameter({
          route: 'appointments/schedule/confirmation/appointment',
          replaceUrl: true,
        })
      : scheduleAppointmentDialogNextStep();

    this.store.dispatch(sendMessage({ messageRequest, continueAction }));
  }

  onCancel() {
    const hasChanged = JSON.stringify(this.requestForm.getRawValue()) !== JSON.stringify(this.initialFormValue);
    const continueAction = this.isMobileBrowser()
      ? loadPageWithPatientIdParameter({ route: 'appointments' })
      : closeScheduleAppointmentDialog();
    if (hasChanged) {
      this.store.dispatch(confirmCancel({ continueAction }));
    } else {
      this.store.dispatch(continueAction);
    }
  }
}
